import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  convertTenantFromDemo,
  type DemoDataBlockerTarget,
  type Tenant,
  type TenantDemoConversion,
} from "./tenantsApi";
import {
  blockerTargets,
  canConvert,
  cannotConvertKey,
  conversionBlockers,
  conversionErrorKey,
  datasetStateKey,
  isBlockedRefusal,
  reasonKey,
  reasonVars,
  targetKindKey,
  type Blocker,
} from "./tenantDemoConversion";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { Modal } from "@web/ui";

// "MUUNNA ASIAKASTENANTIKSI" — the sale closes, the prospect's demo tenant becomes the customer's, and the
// example dataset comes out of it (task e03c428d, owner decision 1.11.2026).
//
// WHY IT IS HERE, under ALUSTAN ASETUKSET, and not a section of its own. It belongs beside the
// *Demotenantti* checkbox because it IS that checkbox's other half: unticking the box over a tenant that
// still holds the dataset is refused by the server (409 `demo_dataset_present`), and the refusal's only
// sensible remedy is this button. Two separated surfaces for one decision would be exactly the arrangement
// that produces an operator hunting for a way to un-mark a tenant.
//
// IT RENDERS NOTHING FOR A CUSTOMER TENANT, which is also how the facts arrive: `exampleDataset` is null
// for every tenant that is not a demo tenant, because the question does not apply there. One read decides
// whether the whole block exists, and the console holds no second copy of that rule.
//
// AND THAT IS WHY THE CONVERSION'S OWN RESULT IS NOT KEPT HERE. A conversion that succeeded turned the demo
// mark off, so this block is unmounted by the very re-read that proves it worked — which took the
// outstanding-key count and the "Yritä uudelleen" button with it when they were drawn here. The result goes
// up through `onConverted` and the page renders it (`TenantShredOutstanding`); what stays here are the two
// endings that CHANGED NOTHING and therefore leave this block standing: a refusal and a transport failure.
//
// THE CONFIRMATION IS A LIST OF WHAT GOES AND WHAT STAYS, and the second half is the one that matters. An
// operator converting a tenant for a paying customer is about to delete rows in it, and the thing they
// need to have read before answering is that the customer's OWN work is not among them. "Two measurements,
// 306 synthetic people, the (demo) units" and "everything else the tenant made stays" are the two bullets;
// the counts are the dataset's by construction, not this tenant's measured ones, which is why they are in
// the sentence rather than interpolated.
//
// NO TYPED CONFIRMATION, unlike the freeze. The freeze shuts a whole organisation out of the product on a
// page reached from a list where four customers read alike, so it asks for the slug. This deletes Verso's
// own demo material and is REFUSED outright if anything of the tenant's names it — so the dangerous
// mistake the slug protects against (acting on the tenant next to the one you meant) ends in a refusal
// here rather than in lost work. What it does leave behind is a tenant that is no longer a demo tenant,
// which is recoverable by ticking the box again.
//
// WHY NO LINKS TO THE BLOCKING OBJECTS, stated because the task asked for them where a target exists. There
// is no addressable one: the authenticated product app carries no slug in its URLs (`/t/:slug/…` is
// pre-session only — `apps/web/src/app/router.tsx`), the console is served on its own host, and the
// operator has no session in the customer's tenant to follow a link with. So each named row is rendered as
// its KIND, its NAME and its id — which is what the operator sends to the customer's pääkäyttäjä, who can
// address it.

/** The confirmation dialog's own state. Absent means the block is in its resting state. */
type Dialog = { submitting: boolean };

/**
 * What the last attempt did, kept so the refusal's list survives a re-render.
 *
 * ONLY THE TWO ENDINGS THAT LEAVE THIS BLOCK ON SCREEN, which is the correction of round 1. A conversion
 * that RAN turns the demo mark off, and this whole block is rendered only for a demo tenant — so a result
 * held here was unmounted by the page's own re-read a moment later, taking the outstanding-key count and
 * the "Yritä uudelleen" button with it (the `incomplete` ending's entire remedy). A result therefore
 * belongs to the PAGE and not to this block: it is reported up through `onConverted` and rendered by
 * `TenantShredOutstanding`, which lives outside the demo-only tree. A refusal and a transport failure
 * change nothing, so the tenant is still a demo tenant and this block is still here to show them.
 */
type Attempt = { kind: "refused"; blockers: Blocker[] } | { kind: "failed"; message: string };

/**
 * What the dialog says goes and what stays, as i18n keys. A list and not a paragraph: an operator reading a
 * confirmation reads the bullets, and the "stays" one is the bullet they most need to have seen.
 */
const CONVERSION_EFFECTS = [
  "tenants.demo.effectMeasurements",
  "tenants.demo.effectPeople",
  "tenants.demo.effectUnits",
  "tenants.demo.effectKeeps",
] as const;

export function TenantDemoSection({
  tenant,
  onConverted,
}: {
  tenant: Tenant;
  /**
   * The conversion ran: the page re-reads the tenant (the demo mark is off, so this block disappears) and
   * says what happened. The RESULT rides along, because `incomplete` is a different sentence from
   * `converted` — and because the outstanding-key count and its retry outlive this block, which the page
   * owns for exactly that reason (see {@link Attempt}).
   */
  onConverted: (result: TenantDemoConversion) => void;
}) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);

  const stateKey = datasetStateKey(tenant.exampleDataset);
  const unavailableKey = cannotConvertKey(tenant);

  const run = useCallback(
    (onFinish: () => void) => {
      convertTenantFromDemo(tenant.id)
        .then(onConverted)
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          if (err instanceof ApiError) {
            // A refusal is not an error state: nothing changed, and what the operator needs is the list of
            // things to get moved. Told apart by the `error` CODE and not by the 409 — the other 409 (a run
            // in progress) carries no blockers and wants a different sentence. A refusal whose extension
            // could not be read still renders as the refusal it is, with an empty list.
            setAttempt(
              isBlockedRefusal(err.problem)
                ? { kind: "refused", blockers: conversionBlockers(err.problem) }
                : { kind: "failed", message: t(conversionErrorKey(err.status, err.problem)) },
            );
            return;
          }

          setAttempt({ kind: "failed", message: t("tenants.demo.error") });
        })
        .finally(onFinish);
    },
    [tenant.id, onConverted, t],
  );

  const onConfirm = useCallback(() => {
    if (dialog?.submitting) return;
    setAttempt(null);
    setDialog({ submitting: true });
    run(() => setDialog(null));
  }, [dialog, run]);

  const titleId = "tenant-demo-confirm-title";
  const bodyId = "tenant-demo-confirm-body";

  return (
    <div className="tenant-demo" data-testid="tenant-demo-conversion">
      {stateKey && (
        <p className="tenants__slug-status" data-testid="tenant-demo-dataset">
          {t("tenants.demo.datasetLabel")} {t(stateKey)}
        </p>
      )}

      <p className="tenant-section__note" role="note">
        {t("tenants.demo.intro")}
      </p>

      {unavailableKey && (
        <p className="tenants__slug-status" data-testid="tenant-demo-unavailable">
          {t(unavailableKey)}
        </p>
      )}

      {attempt?.kind === "failed" && (
        <p className="field-error" role="alert" data-testid="tenant-demo-error">
          {attempt.message}
        </p>
      )}

      {attempt?.kind === "refused" && (
        <div className="banner banner--error" role="alert" data-testid="tenant-demo-blockers">
          <p>{t("tenants.demo.errorBlocked")}</p>
          <ul>
            {attempt.blockers.map((blocker, index) => (
              // The index is in the key because two blockers can legitimately carry the same code — one
              // `foreignQuestionnaire` per questionnaire — and the wire carries no id for the reason
              // itself.
              <li key={`${blocker.code}-${index}`} data-testid="tenant-demo-blocker">
                {t(reasonKey(blocker), reasonVars(blocker))}
                {blockerTargets(blocker).length > 0 && (
                  <ul>
                    {blockerTargets(blocker).map((target) => (
                      <li key={target.id} data-testid="tenant-demo-blocker-target">
                        <BlockerTargetLine target={target} />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tenant-section__actions">
        <button
          type="button"
          className="btn btn--danger"
          onClick={() => {
            setAttempt(null);
            setDialog({ submitting: false });
          }}
          disabled={!canConvert(tenant)}
          data-testid="tenant-demo-convert"
        >
          {t("tenants.demo.convert")}
        </button>
      </div>

      {dialog && (
        <Modal
          role="alertdialog"
          className="tenant-demo__dialog"
          labelledBy={titleId}
          describedBy={bodyId}
          testId="tenant-demo-dialog"
          onClose={() => setDialog(null)}
          closeDisabled={dialog.submitting}
        >
          <h2 id={titleId}>{t("tenants.demo.confirmTitle", { tenant: tenant.slug })}</h2>
          <ul id={bodyId} className="tenant-demo__effects">
            {CONVERSION_EFFECTS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>

          <div className="tenant-section__actions">
            <button
              type="button"
              className="btn"
              onClick={() => setDialog(null)}
              disabled={dialog.submitting}
            >
              {t("tenants.cancel")}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={onConfirm}
              disabled={dialog.submitting}
              data-testid="tenant-demo-confirm"
            >
              {dialog.submitting ? t("tenants.demo.converting") : t("tenants.demo.confirm")}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * One blocking row: what kind of thing it is, what it is called, and its id. The id is shown verbatim and
 * selectable for the reason the tenant's own uuid is on the page header — it is the value an operator
 * passes to somebody who CAN address the row, and this console cannot (see the header comment).
 */
function BlockerTargetLine({ target }: { target: DemoDataBlockerTarget }) {
  const { t } = useTranslation();
  return (
    <>
      {t(targetKindKey(target))}: <strong>{target.name}</strong>{" "}
      <code className="tenants__uuid-value">{target.id}</code>
    </>
  );
}
